import {
  ClickAwayListener,
  Stack,
  Typography,
  useMediaQuery
} from '@mui/material'
import { CalendarStatus } from '@prisma/client'
import Image from 'next/image'
import { useTranslation } from 'next-i18next'
import { PropsWithChildren, useState } from 'react'
import { Popup } from 'react-map-gl'
import { useEventCard } from 'components/Hook/useEventCard'
import { NewFlexContainer } from 'components/common/common.style'
import { AutocompleteItem } from 'interfaces/algolia'
import { themeUtils } from 'styles/theme'

import {
  CalendarIcon,
  CardContainer,
  ImageContainer,
  InstantBadge,
  InstantBadgeContainer,
  ItineraryIcon,
  MobilePopupContent,
  Type
} from './EventPopup.style'

const DesktopPopup = ({
  children,
  popupInfo,
  setPopupInfo
}: PropsWithChildren & EventPopupProps) => (
  <Popup
    latitude={popupInfo.geometry.coordinates[1]!}
    longitude={popupInfo.geometry.coordinates[0]!}
    onClose={() => setPopupInfo(null)}
    anchor="bottom"
    offset={45}
    style={{ padding: '0' }}
  >
    {children}
  </Popup>
)

const MobilePopup = ({
  children,
  setPopupInfo
}: PropsWithChildren & EventPopupProps) => (
  <ClickAwayListener onClickAway={() => setPopupInfo(null)}>
    <MobilePopupContent>{children}</MobilePopupContent>
  </ClickAwayListener>
)

interface EventPopupProps {
  popupInfo: GeoJSON.Feature<GeoJSON.Point, AutocompleteItem>
  setPopupInfo: (
    info: GeoJSON.Feature<GeoJSON.Point, AutocompleteItem> | null
  ) => void
}

export const EventPopup = ({ popupInfo, setPopupInfo }: EventPopupProps) => {
  const { dateDisplayText, distanceLabels, isOver, eventTypes } = useEventCard(
    popupInfo.properties
  )
  const {
    eventCoverImage,
    eventSlug,
    eventName,
    eventIsFeatured,
    editionCalendarStatus
  } = popupInfo.properties
  const { t } = useTranslation('common')
  const [eventCoverImg, setEventCoverImg] = useState<string | null | undefined>(
    eventCoverImage?.replace('http://', 'https://')
  )
  const isMobile = useMediaQuery(themeUtils.maxWidth.md)
  const PopupContainer = isMobile ? MobilePopup : DesktopPopup

  return (
    <PopupContainer popupInfo={popupInfo} setPopupInfo={setPopupInfo}>
      <CardContainer href={`/event/${eventSlug}`} passHref>
        <ImageContainer>
          {eventIsFeatured &&
          !isOver &&
          editionCalendarStatus === CalendarStatus.CONFIRMED ? (
            <InstantBadgeContainer>
              <InstantBadge>{t('event.featured.label')}</InstantBadge>
            </InstantBadgeContainer>
          ) : (
            <Stack />
          )}
          {eventCoverImg ? (
            <Image
              src={eventCoverImg}
              onError={() => {
                setEventCoverImg(null)
              }}
              alt={`${eventName} - image`}
              fill
              style={{ objectFit: 'cover' }}
            />
          ) : null}
        </ImageContainer>
        <NewFlexContainer flexDirection="column" padding="8px 12px" gap="6px">
          <Typography variant="bodyLSemiBold">{eventName}</Typography>
          <NewFlexContainer gap="8px">
            <CalendarIcon />
            <Typography variant="body2" fontSize={14}>
              {dateDisplayText}
            </Typography>
          </NewFlexContainer>
          {distanceLabels.length > 0 && (
            <NewFlexContainer gap="8px">
              <ItineraryIcon />
              <Typography variant="body2" fontSize={14}>
                {distanceLabels.join(', ')}
              </Typography>
            </NewFlexContainer>
          )}
          <NewFlexContainer gap="8px" flexWrap="wrap">
            {eventTypes.map((typeObj, index) => (
              <Type
                key={index}
                $color={typeObj.color}
                $backgroundColor={typeObj.backgroundColor}
              >
                {t(typeObj.value as never)}
              </Type>
            ))}
          </NewFlexContainer>
        </NewFlexContainer>
      </CardContainer>
    </PopupContainer>
  )
}
